/* =========================================================================
   Settings → Feature toggles → Language Support.

   "Enable Language Support" toggle + (when ON) the languages the website
   offers and the default for first-time visitors. Saved together with one
   Save button (POST /config); the backend answers 422 + per-field `errors`.
   Turning the toggle OFF only hides the options -- their values are kept
   and saved as they are.

   RBAC: staff without settings.edit see the values read-only, no Save
   button (the backend also refuses the write with 403).
   ========================================================================= */

import React, { useEffect, useMemo, useState } from "react";
import { MdTranslate } from "react-icons/md";
import styles from "./Settings.module.css";
import { postSettingsSubscription } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { useRbac } from "../../rbac/RbacContext";
import useUnsavedChangesGuard from "./useUnsavedChangesGuard";
import {
  FALLBACK_LANGUAGE,
  LANGUAGES,
  LANGUAGE_FIELD_NAMES,
  fromConfig,
  isLanguageDirty,
  toPayload,
  toggleLanguage,
} from "./languageFields";

const LanguageSettings = ({ config, onDirtyChange, onSaved }) => {
  const { can } = useRbac();
  const canEdit = can("settings", "edit");

  const [saved, setSaved] = useState(() => fromConfig(config));
  const [values, setValues] = useState(() => fromConfig(config));
  const [errors, setErrors] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Re-seed when /config arrives (or is reloaded).
  useEffect(() => {
    const next = fromConfig(config);
    setSaved(next);
    setValues(next);
    setErrors({});
    setNote("");
  }, [config]);

  const dirty = useMemo(() => canEdit && isLanguageDirty(values, saved), [canEdit, values, saved]);
  useUnsavedChangesGuard(dirty);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const clearError = (name) =>
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });

  const onToggle = () => {
    setValues((prev) => ({ ...prev, enable_language_support: !prev.enable_language_support }));
    clearError("enable_language_support");
  };

  const onLanguage = (code, checked) => {
    const result = toggleLanguage(values, code, checked);
    setValues(result.values);
    setNote(result.note);
    clearError("enabled_languages");
    if (result.note) clearError("default_language");
  };

  const onDefault = (code) => {
    setValues((prev) => ({ ...prev, default_language: code }));
    setNote("");
    clearError("default_language");
  };

  const handleSave = async () => {
    const payload = toPayload(values);
    setSaving(true);
    try {
      const res = await postSettingsSubscription(payload);
      const row = res?.data || {};
      // Prefer what the server stored (it normalizes the list order).
      const merged = { ...payload };
      LANGUAGE_FIELD_NAMES.forEach((n) => {
        if (row[n] !== undefined) merged[n] = row[n];
      });
      const next = fromConfig(merged);
      setSaved(next);
      setValues(next);
      setErrors({});
      setNote("");
      showToast("Language support saved.", "success");
      onSaved?.(row);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 422 && data?.errors) {
        setErrors(data.errors);
        showToast(data.message || "Some fields are invalid.", "error");
      } else {
        showToast(data?.message || "Could not save language support.", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const enabledLanguages = LANGUAGES.filter((l) => values.enabled_languages.includes(l.code));

  return (
    <section className={`${styles.card} ${styles.languageCard}`} aria-label="Language support">
      <div className={styles.header}>
        <div className={styles.iconContainer}>
          <MdTranslate className={styles.icon} />
          <div>
            <h4 id="lang-support-label">Enable Language Support</h4>
          </div>
        </div>
        <div>
          <label className={styles.switch}>
            <input
              type="checkbox"
              aria-labelledby="lang-support-label"
              checked={values.enable_language_support}
              onChange={onToggle}
              disabled={!canEdit}
            />
            <span className={styles.slider}></span>
          </label>
        </div>
      </div>
      {errors.enable_language_support && (
        <div role="alert" className={styles.langError}>{errors.enable_language_support}</div>
      )}

      {!canEdit && <div className={styles.langHint}>You have view-only access to Settings.</div>}

      {values.enable_language_support && (
        <div className={styles.langBody}>
          <fieldset className={styles.langFieldset}>
            <legend className={styles.langLegend}>Available languages</legend>
            <div className={styles.langGrid}>
              {LANGUAGES.map((lang) => {
                const isEnglish = lang.code === FALLBACK_LANGUAGE;
                return (
                  <label key={lang.code} className={styles.langOption}>
                    <input
                      type="checkbox"
                      checked={values.enabled_languages.includes(lang.code)}
                      disabled={!canEdit || isEnglish}
                      onChange={(e) => onLanguage(lang.code, e.target.checked)}
                      aria-label={lang.name}
                    />
                    <span className={styles.langFlag} aria-hidden="true">{lang.flag}</span>
                    <span className={styles.langName}>{lang.name}</span>
                    {isEnglish && <span className={styles.langHint}>(always on)</span>}
                  </label>
                );
              })}
            </div>
            {errors.enabled_languages && (
              <div role="alert" className={styles.langError}>{errors.enabled_languages}</div>
            )}
          </fieldset>

          <div className={styles.langDefaultRow}>
            <label htmlFor="lang-default" className={styles.langLegend}>Default language</label>
            <select
              id="lang-default"
              className={`${styles.textInput} ${styles.langSelect} ${errors.default_language ? styles.inputError : ""}`}
              value={values.default_language}
              onChange={(e) => onDefault(e.target.value)}
              disabled={!canEdit}
              aria-invalid={Boolean(errors.default_language)}
              aria-describedby={errors.default_language ? "lang-default-error" : note ? "lang-default-note" : undefined}
            >
              {enabledLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
            {note && !errors.default_language && (
              <div id="lang-default-note" className={styles.langHint} role="status">{note}</div>
            )}
            {errors.default_language && (
              <div id="lang-default-error" role="alert" className={styles.langError}>{errors.default_language}</div>
            )}
          </div>
        </div>
      )}

      {canEdit && (
        <div className={styles.sectionActions}>
          <button type="button" className={styles.primaryBtn} onClick={handleSave} disabled={saving || !dirty}>
            {saving ? "Saving…" : "Save language support"}
          </button>
        </div>
      )}
    </section>
  );
};

export default LanguageSettings;
